import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { BrandLogo } from '@shared/ui/brand-logo';
import { Icon } from '@shared/ui/icon';
import { IconButton } from '@shared/ui/icon-button';
import { Menu, type MenuEntry } from '@shared/ui/menu';
import { SearchInput } from '@shared/ui/search-input';

import {
  GLOBAL_HEADER_MENU_CLOSE_DELAY_MS,
  GLOBAL_HEADER_SEARCH_PANEL_EDGE_PX,
  GLOBAL_HEADER_SEARCH_WIDTH_PX,
  type GlobalHeaderNavTarget,
  STAFF_NAV_ICON_NAME,
  STAFF_NAV_TARGETS,
} from './global-header.constant';
import { GLOBAL_HEADER_TEXT as TEXT } from './global-header.text';

type GlobalHeaderMenu = 'closed' | 'open';

/** 태블릿 햄버거 메뉴에 넣을 계정 항목(AccountMenu 의 항목과 같은 동작) */
export interface GlobalHeaderAccountItem {
  key: string;
  label: string;
  onSelect(): void;
}

export interface GlobalHeaderProps {
  role: 'staff' | 'lp' | 'pf';
  menu?: GlobalHeaderMenu;
  defaultMenu?: GlobalHeaderMenu;
  onMenuChange?(menu: GlobalHeaderMenu): void;
  hasUnread?: boolean;
  searchText?: string;
  onSearchTextChange?(value: string): void;
  onSearchFocusChange?(focused: boolean): void;
  onNotificationClick?(): void;
  onNavigate(target: GlobalHeaderNavTarget): void;
  /** 계정 메뉴(AccountMenu). menu=open 일 때 헤더 아래 오른쪽 끝에 그린다 */
  accountMenu?: ReactNode;
  /**
   * 태블릿(`xl` 미만)의 햄버거 메뉴 뒤쪽 항목 — 설정 아이콘이 숨으므로 계정 메뉴 항목을 여기로 옮긴다.
   * 앞쪽에는 role=staff 의 메뉴 아이콘(포트폴리오 평가 · 파일관리 · 추가데이터 관리 · 멤버관리)이 온다
   */
  accountItems?: readonly GlobalHeaderAccountItem[];
  /** 검색 결과 패널(GlobalSearchPanel). 있으면 헤더 아래 오른쪽 30 에 그린다 */
  searchPanel?: ReactNode;
  /** 알림 목록(NotificationList). 있으면 알림 아이콘 아래 가운데에 그린다 */
  notificationPanel?: ReactNode;
  /** 위쪽 띄움(대행 배너가 있으면 32) */
  topOffset?: number;
}

const NAV_LABEL: Record<(typeof STAFF_NAV_TARGETS)[number], string> = {
  'portfolio-evaluation': TEXT.portfolioEvaluation,
  file: TEXT.file,
  'portfolio-report': TEXT.portfolioReport,
  member: TEXT.member,
};

/**
 * 전역 헤더: 로고 + 회원 유형별 도구(검색 · 알림 · 메뉴 아이콘 · 계정 메뉴).
 * 계정 메뉴 · 검색 패널 · 알림 목록은 슬롯이다 — 띄울지는 상위가 정해 넣는다.
 */
export function GlobalHeader({
  role,
  menu: menuProp,
  defaultMenu = 'closed',
  onMenuChange,
  hasUnread = false,
  searchText,
  onSearchTextChange,
  onSearchFocusChange,
  onNotificationClick,
  onNavigate,
  accountMenu,
  accountItems = [],
  searchPanel,
  notificationPanel,
  topOffset = 0,
}: GlobalHeaderProps) {
  const [menuState, setMenuState] = useState<GlobalHeaderMenu>(defaultMenu);
  const menu = menuProp ?? menuState;
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const staff = role === 'staff';

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  const setMenu = (next: GlobalHeaderMenu) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (next === menu) return;
    if (menuProp === undefined) setMenuState(next);
    onMenuChange?.(next);
  };
  const closeLater = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenu('closed'), GLOBAL_HEADER_MENU_CLOSE_DELAY_MS);
  };
  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && menu === 'open') {
      event.stopPropagation();
      setMenu('closed');
    }
  };

  // 검색 결과 패널은 검색창 왼쪽에 맞추되, 화면 오른쪽을 넘으면 오른쪽 여백 20 에 맞춰 왼쪽으로 당긴다(태블릿)
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const hasSearchPanel = Boolean(searchPanel);
  useLayoutEffect(() => {
    const box = searchBoxRef.current;
    const panel = searchPanelRef.current;
    if (!hasSearchPanel || !box || !panel) return;
    const place = () => {
      const left = box.getBoundingClientRect().left;
      const limit = document.documentElement.clientWidth - GLOBAL_HEADER_SEARCH_PANEL_EDGE_PX;
      const overflow = left + panel.offsetWidth - limit;
      panel.style.left = overflow > 0 ? `${-Math.min(overflow, left)}px` : '0px';
    };
    place();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(panel);
    window.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [hasSearchPanel]);

  // 태블릿 햄버거 메뉴: 숨긴 메뉴 아이콘 + 계정 메뉴 항목을 한 목록으로(누르면 바로 그 동작)
  const tabletMenuItems: MenuEntry[] = [
    ...(staff
      ? STAFF_NAV_TARGETS.map((target) => ({
          key: target,
          label: NAV_LABEL[target],
          onSelect: () => onNavigate(target),
        }))
      : []),
    ...(staff && accountItems.length > 0 ? [{ key: 'separator', type: 'separator' as const }] : []),
    ...accountItems.map((item) => ({ key: item.key, label: item.label, onSelect: item.onSelect })),
  ];

  return (
    <header
      data-role={role}
      data-menu={menu}
      style={{ top: topOffset }}
      className="sticky z-(--z-sticky) w-full"
    >
      <div className="flex h-(--size-row) items-center justify-between bg-bg-surface shadow-chip">
        <button
          type="button"
          aria-label={TEXT.home}
          onClick={() => onNavigate('home')}
          className="flex cursor-pointer items-center px-5 py-3 xl:px-8"
        >
          {/* 이름은 버튼의 aria-label 이 맡는다 — 로고는 장식. 태블릿은 폭 140(PC 182) */}
          <BrandLogo alt="" className="max-w-(--size-button-lg) xl:max-w-none" />
        </button>
        <nav aria-label={TEXT.nav} className="flex items-center gap-3 self-stretch px-5 xl:px-8">
          {staff ? (
            <>
              {/* 헤더 높이를 채우는 자리 — 검색 결과 패널이 헤더 바로 아래, 검색창 왼쪽에 맞춰 붙는다 */}
              {/* 태블릿은 폭 200(`size.input.md`), PC 280 */}
              <div
                ref={searchBoxRef}
                style={{ width: GLOBAL_HEADER_SEARCH_WIDTH_PX }}
                className="relative flex max-w-(--size-input-md) items-center self-stretch xl:max-w-none"
              >
                <SearchInput
                  aria-label={TEXT.search}
                  placeholder={TEXT.searchPlaceholder}
                  value={searchText}
                  onChange={(event) => onSearchTextChange?.(event.target.value)}
                  onFocus={() => onSearchFocusChange?.(true)}
                  onBlur={() => onSearchFocusChange?.(false)}
                  // 헤더 검색창 테두리는 gray-200(border/default) — 입력칸 기본(neutral-100)보다 진하게(2026-10-07 사용자)
                  className="border-border-default"
                />
                {searchPanel ? (
                  <div ref={searchPanelRef} className="absolute top-full left-0 z-(--z-dropdown)">
                    {searchPanel}
                  </div>
                ) : null}
              </div>
              {/* data-slot: 상위가 바깥 누름을 가릴 때 이 안(아이콘 · 알림 목록)은 빼고 본다 */}
              <div data-slot="header-notification" className="relative">
                <IconButton
                  aria-label={TEXT.notification}
                  aria-expanded={notificationPanel ? true : false}
                  variant="ghost"
                  size="sm"
                  showDot={hasUnread}
                  icon={<Icon name="bell" size="lg" />}
                  onClick={onNotificationClick}
                />
                {notificationPanel ? (
                  // 태블릿은 알림 아이콘 오른쪽 끝에 맞춘다(가운데면 화면 오른쪽을 넘는다), PC 는 가운데
                  <div className="absolute top-full right-0 z-(--z-dropdown) xl:right-auto xl:left-1/2 xl:-translate-x-1/2">
                    {notificationPanel}
                  </div>
                ) : null}
              </div>
              {STAFF_NAV_TARGETS.map((target) => (
                <IconButton
                  key={target}
                  aria-label={NAV_LABEL[target]}
                  icon={<Icon name={STAFF_NAV_ICON_NAME[target]} size="lg" />}
                  variant="ghost"
                  size="sm"
                  // 태블릿에서는 햄버거 메뉴로 옮긴다
                  className="hidden xl:inline-flex"
                  onClick={() => onNavigate(target)}
                />
              ))}
            </>
          ) : null}
          {/* 설정(계정 메뉴)도 태블릿에서는 햄버거 메뉴로 옮긴다 */}
          <div
            className="hidden xl:block"
            onMouseEnter={() => setMenu('open')}
            onMouseLeave={closeLater}
            onFocus={() => setMenu('open')}
            onKeyDown={handleMenuKeyDown}
          >
            <IconButton
              aria-label={TEXT.settings}
              aria-haspopup="menu"
              aria-expanded={menu === 'open'}
              icon={<Icon name="settings" size="lg" />}
              variant="ghost"
              size="sm"
              // 기존 서비스처럼 누르면 열기만 한다 — 마우스를 올려 열린 상태에서 누르면 닫히던 문제(닫기는 Esc · 바깥으로 나감)
              onClick={() => setMenu('open')}
            />
          </div>
          {tabletMenuItems.length > 0 ? (
            <div className="flex xl:hidden">
              <Menu
                items={tabletMenuItems}
                aria-label={TEXT.tabletMenu}
                trigger={
                  <IconButton
                    aria-label={TEXT.tabletMenu}
                    icon={<HamburgerIcon />}
                    variant="ghost"
                    size="sm"
                  />
                }
              />
            </div>
          ) : null}
        </nav>
      </div>
      {menu === 'open' && accountMenu ? (
        <div
          onMouseEnter={() => setMenu('open')}
          onMouseLeave={closeLater}
          onKeyDown={handleMenuKeyDown}
          className="absolute top-(--size-row) right-0 z-(--z-dropdown)"
        >
          {accountMenu}
        </div>
      ) : null}
    </header>
  );
}

/**
 * 햄버거(세 줄) 아이콘 — 아이콘 세트(`icon.glyphs.ts`, Figma 생성물)에 없어 여기서 그린다.
 * Icon 과 같은 규칙: viewBox 24 · lg(24) · currentColor · 선 1.5(non-scaling)
 */
function HamburgerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      focusable="false"
      aria-hidden="true"
      data-icon="menu"
      className="inline-block size-(--size-icon-lg) shrink-0"
    >
      <path
        d="M4 6H20M4 12H20M4 18H20"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
